import { FC, useMemo } from 'react';
import {
  SideSheet,
  SideSheetSize,
  SideSheetClose,
  SideSheetHeader,
  ButtonSize,
  SideSheetBody,
  Box,
  SideSheetFooter,
  Text,
  theme,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { useName } from '@bechtel/client-common';
import { object, string } from 'yup';
import { Formik } from 'formik';
import { capitalize, find, get } from 'lodash';

import { FooterButtonStyled, AvatarRegularStyled } from './styled';
import { AddCommentSidesheetProps, AddCommentFormikState } from './types';
import { AddComment } from '~/components';
import { useTimesheet } from '~/detail/context';
import { useTimeSheetReason } from '~/hooks';
import { TimesheetReasons } from '~/detail/types';

export const AddCommentSidesheet: FC<AddCommentSidesheetProps> = ({
  timeEntryInput,
  crewMember,
  closeSidesheet,
  onSubmit,
}) => {
  const { getFullName } = useName();
  const { hourTypes } = useTimesheet();
  const hourDescription = useMemo(
    () => find(hourTypes, (hour) => hour.hourCode === timeEntryInput.hourCode)?.description || '',
    [timeEntryInput.hourCode]
  );
  const { data } = useTimeSheetReason();
  const reasonList = get(data, 'timesheetReasons', []);
  const otherReasonId = find(reasonList, (reason) => reason.name == TimesheetReasons.TIMESHEET_REASON_OTHER)?.id;
  const validationSchema = object().shape({
    commentId: string().required(t`Reason is mandatory`),
    otherCommentDescription: string().when('commentId', {
      is: (commentId: string) => commentId === otherReasonId,
      then: string()
        .required('Comment is required')
        .max(2000, t`Too Long!`),
      otherwise: string().max(2000, t`Too Long!`),
    }),
  });

  return (
    <SideSheet onDismiss={closeSidesheet} size={SideSheetSize.DEFAULT} visible>
      <SideSheetHeader title={t`Add a Comment`}>
        <SideSheetClose onClick={closeSidesheet} size={ButtonSize.SMALL}></SideSheetClose>
      </SideSheetHeader>
      <Formik
        initialValues={{
          commentId: get(timeEntryInput, 'commentId', '') as string,
          otherCommentDescription: get(timeEntryInput, 'otherCommentDescription', '') as string,
        }}
        validationSchema={validationSchema}
        onSubmit={(values: AddCommentFormikState) => {
          onSubmit({
            ...values,
            otherCommentDescription: values.commentId === otherReasonId ? values.otherCommentDescription : '',
          });
        }}
      >
        {({ isValid, dirty, handleSubmit, setFieldValue, errors, values }) => {
          return (
            <>
              <SideSheetBody>
                <Box flexDirection={{ sm: 'column' }} css={{ width: '100%' }}>
                  <Box
                    p={{ sm: '500' }}
                    flexDirection={{ sm: 'column' }}
                    css={`
                      background-color: ${theme.colors.bechtel.blue[100]};
                    `}
                  >
                    <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'start' }}>
                      <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'start' }} gap={{ sm: '400' }}>
                        <Box alignSelf={{ sm: 'end' }}>
                          <AvatarRegularStyled
                            data-testid="avatar"
                            src={crewMember?.imageUrl}
                            hasImageUrl={!!crewMember?.imageUrl}
                          />
                        </Box>
                        <Box flexDirection={{ sm: 'column' }} justifyContent={{ sm: 'center' }}>
                          <Text
                            size="300"
                            color="grey.1000"
                            weight="600"
                            family="graphikCompact"
                            style={{ lineHeight: 1.67 }}
                          >
                            {getFullName(crewMember.firstName, crewMember.lastName)}
                          </Text>
                          <Text size="200" color="grey.600" family="graphikCompact" style={{ lineHeight: 1.2 }}>
                            {capitalize(crewMember.badgeNumber || '')}
                          </Text>
                          <Text size="200" color="grey.600" family="graphikCompact" style={{ lineHeight: 1.2 }}>
                            {capitalize(crewMember.persona || '')}
                          </Text>
                        </Box>
                      </Box>
                      <Box pr={{ sm: '400' }}>
                        <Text size="600" color="grey.1000" family="graphikCompact" style={{ alignSelf: 'end' }}>
                          {timeEntryInput.hourCode}
                        </Text>
                        <Text size="900" color="grey.1000" family="graphikCompact" style={{ alignSelf: 'start' }}>
                          {timeEntryInput.hours}
                        </Text>
                      </Box>
                    </Box>
                    <Box mt={{ sm: '500' }}>
                      <Text size="500" color="grey.1000" weight="600">
                        Cost Code:
                      </Text>
                      <Text size="500" color="grey.1000">
                        &nbsp;{timeEntryInput.costCodeId}
                      </Text>
                    </Box>
                  </Box>
                  <Box
                    pl={{ sm: '500' }}
                    pt={{ sm: '550' }}
                    pb={{ sm: '450' }}
                    css={`
                      background-color: ${theme.colors.grey[200]};
                    `}
                  >
                    <Text family={'graphik'} size="400" weight={'600'} color="grey.1000">
                      Provide a reason for charging {hourDescription}
                    </Text>
                  </Box>
                  <Box px={{ sm: '500' }} py={{ sm: '700' }} flexDirection={{ sm: 'column' }}>
                    <AddComment
                      comment={values.otherCommentDescription}
                      reason={values.commentId}
                      setReason={(reason) => setFieldValue('commentId', reason.id)}
                      setComment={(comment) => setFieldValue('otherCommentDescription', comment)}
                    />
                    {errors.otherCommentDescription && (
                      <Box mt={{ sm: '400' }}>
                        <Text color="bechtel.red">
                          <Trans>{errors.otherCommentDescription}</Trans>
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Box>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    <FooterButtonStyled onClick={closeSidesheet}>
                      <Trans>Cancel</Trans>
                    </FooterButtonStyled>
                  </Box>
                  <FooterButtonStyled onClick={() => handleSubmit()} disabled={!dirty || !isValid}>
                    <Trans>Update Time</Trans>
                  </FooterButtonStyled>
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
    </SideSheet>
  );
};
